import { must } from "@/common/utils/must";
import objectUtils from "@/common/utils/objectUtils";
import { shopWindowTypes } from "@/modules/Constants";
import type ExperienceData from "@/modules/ExperienceData";

export const openingSlideType = {
  NONE: "none",
  HINTS: "hints",
  CUSTOM: "custom",
};

/** Where a slide index points: a cluster, the cluster index and the item index. */
export interface ClusterArticleIndex {
  clusterName: string;
  cindex?: number;
  index: number;
  cta?: unknown;
}

// Type-specific settings; every type may set openingSlideType.
export interface ShopWindowData {
  openingSlideType?: string;
  clusterName?: string;
  [key: string]: unknown;
}

export default class ShopWindowConfig {
  declare name: string;
  declare type: string;
  declare title: string;
  declare clusterName: string;
  declare icon: unknown;
  declare isPortraitOverlay: boolean;
  declare data: ShopWindowData;
  /** Current slide index; set by init(). */
  declare index: number;
  /** Set by plugins on some configs, e.g. `{ cta }`. */
  declare links: { cta?: unknown; [key: string]: unknown } | undefined;

  constructor(
    name: string,
    type: string,
    title: string,
    clusterName: string,
    icon: unknown,
    isPortraitOverlay: boolean,
    data: ShopWindowData,
  ) {
    // value is passed in toggle button event and cta event
    this.name = name;
    // Constants.shopWindowTypes
    this.type = type;
    // Display title
    this.title = title;
    // name of the experience cluster or timeline cluster (timelineName)
    this.clusterName = clusterName;
    // String replaces with icon-<icon>
    this.icon = icon;
    // The shop window overlays on portrait
    this.isPortraitOverlay = isPortraitOverlay;
    // data depends on the type
    this.data = data;
  }

  /** A fresh config with the template's fields; the extend copies the rest. */
  static fromTemplate(template: ShopWindowConfig): ShopWindowConfig {
    return objectUtils.extend(
      new ShopWindowConfig(
        template.name,
        template.type,
        template.title,
        template.clusterName,
        template.icon,
        template.isPortraitOverlay,
        template.data,
      ),
      template,
    );
  }

  hasSlides() {
    return (
      this.type === shopWindowTypes.METHOD ||
      this.type === shopWindowTypes.PRODUCT
    );
  }

  openingSlideType() {
    return this.data.openingSlideType || openingSlideType.NONE;
  }

  hasOpeningSlide(experienceData?: ExperienceData): boolean {
    return this.openingSlideType() !== openingSlideType.NONE;
  }

  showOpeningSlide(experienceData: ExperienceData): boolean {
    return this.index === 0 && this.hasOpeningSlide(experienceData);
  }

  getTimelineData(experienceData: ExperienceData) {
    const clusterIndex = this.clusterArticleIndexAt(experienceData, this.index);
    return experienceData.getTimelineItem(
      clusterIndex.clusterName,
      clusterIndex.cindex,
      clusterIndex.index,
    );
  }

  clusterNames() {
    return [this.clusterName];
  }

  slideCount(experienceData: ExperienceData): number {
    if (this.hasSlides()) {
      let count = experienceData.getTotalClusterSize(this.clusterName);
      if (this.hasOpeningSlide(experienceData)) {
        count += 1;
      }
      return count;
    } else {
      return 0;
    }
  }

  init(experienceData?: ExperienceData): this {
    this.index = 0;

    return this;
  }

  toClusterIndex(
    experienceData: ExperienceData,
    name: string,
    articleIndex: number,
  ): ClusterArticleIndex {
    // Every index maps into cluster 0.
    return {
      clusterName: name,
      cindex: 0,
      index: articleIndex,
    };
  }

  /*
   * converts from a slide index to a:
   * clusterArticleIndex {
   *   clusterName: <stirng - name of cluster>
   *   cindex: <int - index of the cluster>
   *   index: <int - index of items within the cluster at cindex>
   *   cta: <link - cta link>
   * }
   */
  toClusterArticleIndex(
    experienceData: ExperienceData,
    index: number,
  ): ClusterArticleIndex | null {
    if (index < 0) {
      return null;
    }

    const clusterSize = experienceData.getTotalClusterSize(this.clusterName);

    if (this.hasOpeningSlide(experienceData)) {
      index -= 1;
    }

    if (index < 0) {
      return { clusterName: "_hint", index: index };
    }

    if (clusterSize === 0 || index < clusterSize) {
      const clusterIndex = this.toClusterIndex(
        experienceData,
        this.clusterName,
        index,
      );

      return {
        clusterName: this.clusterName,
        cindex: clusterIndex.cindex,
        index: clusterIndex.index,
        cta: this.links ? this.links.cta : undefined,
      };
    }

    return null;
  }

  /** toClusterArticleIndex for an index within range(). */
  clusterArticleIndexAt(
    experienceData: ExperienceData,
    index: number,
  ): ClusterArticleIndex {
    return must(
      this.toClusterArticleIndex(experienceData, index),
      `shop window slot ${index}`,
    );
  }

  incrementShopWindowIndex(experienceData: ExperienceData): boolean {
    return this.setShopWindowIndex(experienceData, this.index + 1);
  }

  decrementShopWindowIndex(experienceData: ExperienceData): boolean {
    return this.setShopWindowIndex(experienceData, this.index - 1);
  }

  setShopWindowIndex(experienceData: ExperienceData, index: number): boolean {
    const range = this.range(experienceData);
    if (index >= range.min && index < range.max) {
      this.index = index;
      return true;
    }
    return false;
  }

  mainIndexStart(experienceData: ExperienceData): number {
    let min = 0;
    if (this.hasOpeningSlide(experienceData)) {
      min += 1;
    }
    return min;
  }

  mainRange(experienceData: ExperienceData): { min: number; max: number } {
    if (this.hasSlides()) {
      let min = 0;
      const count = experienceData.getTotalClusterSize(this.clusterName);
      if (this.hasOpeningSlide(experienceData)) {
        min += 1;
      }
      return { min: min, max: min + count };
    } else {
      return { min: 0, max: 0 };
    }
  }

  range(experienceData: ExperienceData): { min: number; max: number } {
    const size = this.slideCount(experienceData);
    return { min: 0, max: size };
  }
}
